import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { HealthCheck, HealthFinding, HealthSeverity } from '@accounting/shared';
import { useCompanies } from '../../hooks/useCompanies';
import { useBooksHealth } from '../../hooks/useBooksHealth';
import { setActiveCompanyId } from '../../stores/useCompanyStore';
import { todayIso } from '../../lib/formatters';

/**
 * One page that answers "are my books right today?" across every company.
 *
 * A single-owner book has no second reviewer, so nothing else in the system
 * distinguishes "quiet because everything is fine" from "quiet because nobody
 * looked". `fail` means provably wrong — don't trust a report until it clears.
 * `warn` means needs attention.
 */
export const Route = createFileRoute('/_auth/books-health')({
  component: BooksHealthPage,
});

const TAG_CLASS: Record<HealthSeverity, string> = {
  ok: 'tag ok',
  warn: 'tag warn',
  fail: 'tag bad',
};

const TAG_LABEL: Record<HealthSeverity, string> = {
  ok: 'OK',
  warn: 'Attention',
  fail: 'Failed',
};

const OVERALL_COPY: Record<HealthSeverity, { title: string; body: string }> = {
  ok: {
    title: 'Books look healthy',
    body: 'Every check passed. Nothing needs attention right now.',
  },
  warn: {
    title: 'Needs attention',
    body: 'Nothing is provably wrong, but some items have drifted and should be cleared.',
  },
  fail: {
    title: 'Something is wrong',
    body: 'At least one check proves the books are incorrect. Resolve these before relying on any report.',
  },
};

function BooksHealthPage() {
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const [asOf, setAsOf] = useState(todayIso());

  const ids = companies.map((c) => c.id);
  const { data, isLoading, error, refetch, isFetching } = useBooksHealth(ids, asOf);

  if (companiesLoading) return <p className="muted">Loading…</p>;

  if (companies.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Books Health</h1>
        <p className="muted">No companies yet — nothing to check.</p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 style={{ margin: 0 }}>Books Health</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {companies.length} {companies.length === 1 ? 'company' : 'companies'} checked
          </p>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            As of
          </span>
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            max={todayIso()}
          />
        </label>
        <button type="button" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {error && (
        <div className="card" style={{ marginBottom: 16 }}>
          <p style={{ margin: 0 }}>Could not run the health check.</p>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {isLoading && !data && <p className="muted">Running checks…</p>}

      {data && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className={TAG_CLASS[data.overall]}>{TAG_LABEL[data.overall]}</span>
              <strong>{OVERALL_COPY[data.overall].title}</strong>
            </div>
            <p className="muted" style={{ margin: '8px 0 0' }}>
              {OVERALL_COPY[data.overall].body}
            </p>
          </div>

          {/* Failures first — a warn is never more urgent than a proven error. */}
          {[...data.checks]
            .sort((a, b) => rank(b.severity) - rank(a.severity))
            .map((check) => (
              <CheckCard key={check.id} check={check} />
            ))}
        </>
      )}
    </div>
  );
}

function rank(s: HealthSeverity): number {
  return s === 'fail' ? 2 : s === 'warn' ? 1 : 0;
}

function CheckCard({ check }: { check: HealthCheck }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className={TAG_CLASS[check.severity]}>{TAG_LABEL[check.severity]}</span>
        <strong style={{ flex: 1, minWidth: 200 }}>{check.label}</strong>
        <span className="muted" style={{ fontSize: 13 }}>
          {check.summary}
        </span>
      </div>

      {check.findings.length > 0 && (
        <ul style={{ margin: '12px 0 0', paddingLeft: 0, listStyle: 'none' }}>
          {check.findings.map((f, i) => (
            <FindingRow key={`${f.companyId}-${i}`} finding={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FindingRow({ finding }: { finding: HealthFinding }) {
  const navigate = useNavigate();

  /**
   * The pages that resolve a finding are all scoped to the active company, so
   * jumping straight there would show the wrong company's data. Switch the
   * active company first, then navigate.
   */
  const goFix = () => {
    if (!finding.link) return;
    setActiveCompanyId(finding.companyId);
    void navigate({ to: finding.link });
  };

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}
    >
      <strong style={{ minWidth: 160 }}>{finding.companyName}</strong>
      <span style={{ flex: 1, minWidth: 220 }}>{finding.detail}</span>
      {finding.link && (
        <button type="button" onClick={goFix}>
          Fix
        </button>
      )}
    </li>
  );
}
